import React, { useState } from 'react';
import { ContentTemplate, HtmlTrans, SectionHeader, Typography } from 'web-library';
import { Theme as AugmentedTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import Declaration from '../../../../components/Declaration';
import NavigationButtons from '../../../../components/NavigationButtons';
import RouteKey from '../../../../routes/key';
import SummaryContent from './SummaryContent';
import { usePutConfirmWithdrawal } from '../../../../api/candidate/exam/@coordinator/queries-services/queries';
import DraftTimeStamp from '../../../../components/DraftTimeStamp';
import { ExamType } from '../../../../api/candidate/exam/model';

interface ReviewProps {
  lastUpdatedTime?: string;
  examTypeCode?: string;
}

const { withdrawal } = RouteKey.child;
const { selectCandidates, submitted, withdrawal: withdrawalRoute } = RouteKey.withdrawal.child;

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  marginBottom4: {
    marginBottom: theme.spacing(4),
  },
  errorText: {
    color: theme.color.state.stateError,
  },
}));

const getDeclarationContent = (t: TFunction<'translation'>, examTypeCode: string | undefined) => {
  if (examTypeCode && examTypeCode === ExamType.GCE) {
    return t('withdrawal->>Coordinator.Summary.Review.DeclarationMessageGce');
  }
  if (examTypeCode && examTypeCode === ExamType.IPSLE) {
    return <HtmlTrans i18nKey="withdrawal->>Coordinator.Summary.Review.DeclarationMessageIpsle" />;
  }
  return t('withdrawal->>Coordinator.Summary.Review.DeclarationMessagePsle');
};

const Review = ({ lastUpdatedTime, examTypeCode }: ReviewProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [isAgreed, setIsAgreed] = useState(false);
  const [error, setError] = useState('');
  const [statusError, setStatusError] = useState(false);

  const { mutate: confirmWithdrawal } = usePutConfirmWithdrawal(
    () => {
      history.push(`/${withdrawal}/${withdrawalRoute.route}/${uuid}/${submitted.route}`);
    },
    () => {
      setError(t('withdrawal->>Coordinator.Summary.Review.ConfirmError'));
    }
  );

  const sectionHeader = (
    <SectionHeader title={t('withdrawal->>Coordinator.Summary.Review.SectionHeader')} />
  );

  const handleAgreement = () => {
    setError('');
    setIsAgreed(!isAgreed);
  };

  const handleProceed = () => {
    if (statusError) {
      setError(t('withdrawal->>Coordinator.Summary.Review.StatusError'));
    } else if (isAgreed) {
      confirmWithdrawal(uuid);
    } else {
      setError(t('withdrawal->>ReviewWithdrawal.DeclarationError'));
    }
  };

  return (
    <>
      <SummaryContent
        uuid={uuid}
        sectionHeader={sectionHeader}
        setError={setError}
        setStatusError={setStatusError}
      />
      <ContentTemplate isDividerTop type="large">
        <Typography variant="h2" className={classes.marginBottom4}>
          {t('withdrawal->>Coordinator.Summary.Review.Declaration')}
        </Typography>
        <Declaration
          noTitle
          isAgreed={isAgreed}
          handleAgreement={handleAgreement}
          customContent={getDeclarationContent(t, examTypeCode)}
        />
        {error && (
          <Typography className={classes.errorText} variant="body2">
            {error}
          </Typography>
        )}
        <NavigationButtons
          backLabel={t('withdrawal->>Coordinator.Summary.Review.Back')}
          procceedLabel={t('withdrawal->>Coordinator.Summary.Review.ConfirmWithdrawal')}
          onBackClick={() =>
            history.push(
              `/${withdrawal}/${withdrawalRoute.route}/${uuid}/${selectCandidates.route}`
            )
          }
          onProceedClick={handleProceed}
        />
        {lastUpdatedTime && <DraftTimeStamp savedAt={lastUpdatedTime} />}
      </ContentTemplate>
    </>
  );
};

export default Review;
